"use client";

import { useMemo } from "react";
import { pick6Group } from "@/lib/fantasy-props/book-links";
import { fetchPick6Quote, pick6Picks, type Pick6Quote } from "@/lib/fantasy-props/pick6-quote";
import { legHref, priceLadder, sameLadder, slipsStayInOneLeague, topPayout, type SlipLegView, type SlipPrice, type SlipTarget } from "@/lib/fantasy-props/slip";
import { useSlipQuote, type QuoteState, type SlipPricing } from "./use-slip-quote";

export type Pick6QuoteState =
  | { readonly status: "off" }
  | { readonly status: "mixed" }
  | { readonly status: "unreadable" }
  | QuoteState<Pick6Quote>;

const OFF = { status: "off" } as const;
const MIXED = { status: "mixed" } as const;
const UNREADABLE = { status: "unreadable" } as const;

export function usePick6Quote(book: string | null, legs: readonly SlipLegView[]): Pick6QuoteState {
  const priced = book !== null && slipsStayInOneLeague(book) && legs.length >= 2;
  const read = useMemo(() => {
    if (!priced) return null;
    const links = legs.map((leg) => legHref(leg, false));
    return new Set(links.flatMap((link) => pick6Group(link) ?? [])).size > 1 ? "mixed" : pick6Picks(links);
  }, [priced, legs]);
  const picks = read === "mixed" ? null : read;
  const quote = useSlipQuote(picks, fetchPick6Quote);

  if (!priced) return OFF;
  if (read === "mixed") return MIXED;
  if (picks === null) return UNREADABLE;
  return quote;
}

export function pick6Pricing(state: Pick6QuoteState, target: SlipTarget | null, price: SlipPrice): SlipPricing {
  const none: SlipPricing = { live: null, price, tiers: undefined, mismatch: false };
  if (state.status === "off" || target === null) return none;
  if (state.status === "pricing") return { ...none, live: { status: "pricing" } };
  if (state.status !== "priced") return { ...none, live: { status: "unavailable", reason: state.status } };
  const tiers = state.quote.tables[target.legs];
  if (tiers === undefined) return { ...none, live: { status: "unavailable", reason: "unoffered" }, mismatch: true };
  const factor = state.quote.factor;
  const uncutPayout = topPayout(priceLadder(target, { ...price, factor: 1 }, tiers));
  return { live: { status: "priced", factor, uncutPayout, stale: state.stale }, price: { ...price, factor }, tiers, mismatch: !sameLadder(tiers, target.base) };
}
